/**
 * 예비기사 분배 — 차주가 「이 예비기사에게 얼마」를 적는다(2026-09-28 way · 회의 26·27).
 * 적은 금액은 회계의 「차인지급액 조정」에서 차주 몫에서 빠지고 그 기사에게 따로 지급된다(`functions/src/driverReserve.ts`).
 *
 * 🔴 차주 본인만(서버가 휴대폰 번호로 가른다) · 내 차에 편성된 기사만 · 합계는 내 차인지급액 안에서 · 회계가 확정한 달은 보기만.
 * 저장은 그 달 분배를 **통째로** 바꾼다(0 이나 빈칸 = 그 기사 빼기).
 */

import { useEffect, useMemo, useState } from 'react';
import { driverApi, type ReserveSplitView } from '../lib/driverApi';
import Screen, { Empty, ErrorBanner, Loading } from './Screen';
import { monthLabel, won } from './format';

const digitsOnly = (v: string) => v.replace(/[^\d]/g, '');

export default function ReserveSplit() {
  const [view, setView] = useState<ReserveSplitView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [amounts, setAmounts] = useState<Record<number, string>>({});

  async function load(args: { driveMon?: string; ownerId?: number } = {}) {
    setError(null);
    try {
      const v = await driverApi.getReserveSplit(args);
      setView(v);
      setAmounts(Object.fromEntries((v.splits ?? []).map((s) => [s.driver_id, String(Math.round(Number(s.amount)))])));
    } catch (e) {
      setError(e instanceof Error ? e.message : '불러오지 못했습니다.');
      setView((cur) => cur ?? { owners: [], months: [], driveMon: null, ownerId: null });
    }
  }
  useEffect(() => { void load(); }, []);

  // 후보 = 내 차 기사 + 이미 적어 둔 기사(편성이 끝났어도 보이게).
  const people = useMemo(() => {
    const list = (view?.candidates ?? []).map((c) => ({ id: c.driver_id, name: c.driver_name, car: c.car_no }));
    for (const s of view?.splits ?? []) {
      if (!list.some((p) => p.id === s.driver_id)) list.push({ id: s.driver_id, name: s.driver_name ?? '', car: null });
    }
    return list;
  }, [view]);
  const total = Object.values(amounts).reduce((s, v) => s + (Number(digitsOnly(v)) || 0), 0);
  const limit = view?.limit ?? null;
  const over = limit !== null && total > limit;
  const readOnly = !!view?.closed;
  const pending = !!view?.pending;

  async function save() {
    if (!view?.driveMon || !view.ownerId) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const items = people.map((p) => ({ driverId: p.id, amount: digitsOnly(amounts[p.id] ?? '') || '0' }));
      const r = await driverApi.saveReserveSplit({ driveMon: view.driveMon, ownerId: view.ownerId, items });
      setNotice(r.total > 0 ? `저장했습니다. 합계 ${won(r.total)}원` : '분배를 모두 지웠습니다.');
      await load({ driveMon: view.driveMon, ownerId: view.ownerId });
    } catch (e) {
      setError(e instanceof Error ? e.message : '저장하지 못했습니다.');
    } finally { setBusy(false); }
  }

  return (
    <Screen title="예비기사 분배">
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {view === null ? (
        <Loading />
      ) : view.owners.length === 0 ? (
        <Empty>차주로 등록된 번호가 아닙니다.</Empty>
      ) : !view.driveMon ? (
        <Empty>나눌 수 있는 명세서가 아직 없습니다.</Empty>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {view.owners.length > 1 && (
              <select className="field min-h-touch flex-1 text-lg" value={view.ownerId ?? ''}
                      onChange={(e) => void load({ ownerId: Number(e.target.value) })}>
                {view.owners.map((o) => <option key={o.owner_id} value={o.owner_id}>{o.owner_name}</option>)}
              </select>
            )}
            <select className="field min-h-touch flex-1 text-lg" value={view.driveMon}
                    onChange={(e) => void load({ driveMon: e.target.value, ownerId: view.ownerId ?? undefined })}>
              {view.months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </div>

          <section className="driver-card px-5 py-4">
            <p className="text-base text-ink-soft">나눌 수 있는 금액(내 차인지급액)</p>
            {pending ? (
              <p className="mt-1 text-lg font-semibold text-ink">명세서 공개 전</p>
            ) : (
              <p className="mt-1 text-2xl font-bold text-ink num">{limit === null ? '-' : `${won(limit)}원`}</p>
            )}
            <p className="mt-2 text-base text-ink-soft">
              {pending
                ? '금액은 명세서가 공개되면 보입니다. 지금 미리 적어 두실 수 있고, 내 지급액보다 많으면 저장되지 않습니다.'
                : '적은 금액은 내 지급액에서 빠지고 그 기사님께 따로 지급됩니다.'}
            </p>
          </section>

          {readOnly && (
            <p className="rounded-xl bg-amber-50 px-4 py-3 text-base text-amber-800">
              회계에서 이 달을 확정해 바꿀 수 없습니다.
            </p>
          )}

          {people.length === 0 ? (
            <Empty>내 차량에 편성된 다른 기사가 없습니다.</Empty>
          ) : (
            <ul className="space-y-3">
              {people.map((p) => (
                <li key={p.id} className="driver-card flex items-center gap-3 px-5 py-4">
                  <span className="min-w-0 flex-1">
                    <span className="block text-lg font-semibold text-ink">{p.name}</span>
                    {p.car && <span className="block text-base text-ink-faint">{p.car}</span>}
                  </span>
                  <input
                    inputMode="numeric"
                    className="field min-h-touch w-40 text-right text-lg num"
                    placeholder="0"
                    disabled={readOnly}
                    value={amounts[p.id] ? Number(digitsOnly(amounts[p.id]!)).toLocaleString() : ''}
                    onChange={(e) => setAmounts((a) => ({ ...a, [p.id]: digitsOnly(e.target.value) }))}
                  />
                  <span className="text-base text-ink-soft">원</span>
                </li>
              ))}
            </ul>
          )}

          <div className={`flex items-center justify-between rounded-xl px-5 py-3 ${over ? 'bg-red-50 text-red-700' : 'bg-canvas text-ink'}`}>
            <span className="text-base">합계</span>
            <span className="text-xl font-bold num">{won(total)}원</span>
          </div>
          {over && <p className="text-base text-red-700">나눌 수 있는 금액보다 많습니다.</p>}
          {notice && <p className="rounded-xl bg-emerald-50 px-4 py-3 text-base text-emerald-800">{notice}</p>}

          {!readOnly && people.length > 0 && (
            <button className="min-h-touch w-full rounded-2xl bg-brand-600 text-lg font-bold text-white disabled:opacity-50"
                    disabled={busy || over} onClick={() => void save()}>
              {busy ? '저장 중…' : '저장'}
            </button>
          )}
        </div>
      )}
    </Screen>
  );
}
